'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AdminSettings {
  companyName: string
  companyEmail: string
  companyPhone: string
  companyAddress: string
  companyCity: string
  companyState: string
  companyZip: string
  linkedinUrl: string
  facebookUrl: string
  instagramUrl: string
  businessHours: string
  serviceArea: string
  fleetSize: string
  smtpHost: string
  smtpPort: string
  smtpUsername: string
  smtpPassword: string
  fromEmail: string
  fromName: string
  sessionTimeout: number
  requireTwoFactor: boolean
  passwordMinLength: number
  googleAnalyticsId: string
  enableEmailNotifications: boolean
  enableLeadTracking: boolean
  enablePerformanceMonitoring: boolean
}

interface SettingsContextType {
  settings: AdminSettings | null
  loading: boolean
  error: string | null
  refreshSettings: () => Promise<void>
}

const defaultSettings: AdminSettings = {
  companyName: 'Spitzer Helicopters',
  companyEmail: 'info@spitzerair.com',
  companyPhone: '(310) 614-0986',
  companyAddress: '5158 Clareton Dr, Unit 1147',
  companyCity: 'Agoura Hills',
  companyState: 'CA',
  companyZip: '91376',
  linkedinUrl: 'https://linkedin.com/company/spitzer-helicopters',
  facebookUrl: 'https://facebook.com/spitzerhelicopters',
  instagramUrl: 'https://instagram.com/spitzerhelicopters',
  businessHours: '24/7 Support Available',
  serviceArea: 'North America',
  fleetSize: '50+ Aircraft',
  smtpHost: 'smtp.gmail.com',
  smtpPort: '587',
  smtpUsername: 'noreply@spitzerhelicopters.com',
  smtpPassword: '',
  fromEmail: 'noreply@spitzerhelicopters.com',
  fromName: 'Spitzer Helicopters',
  sessionTimeout: 30,
  requireTwoFactor: false,
  passwordMinLength: 8,
  googleAnalyticsId: '',
  enableEmailNotifications: true,
  enableLeadTracking: true,
  enablePerformanceMonitoring: true
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AdminSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Only fetch settings on admin pages to avoid 404 errors on public pages
      const isAdminPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')
      
      if (isAdminPage) {
        const response = await fetch('/api/admin/settings', { 
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }
        })
        
        if (response.ok) {
          const data = await response.json()
          setSettings(data)
        } else {
          setSettings(defaultSettings)
        }
      } else {
        // Use default settings for public pages without making API call
        setSettings(defaultSettings)
      }
    } catch (error) {
      // Silently use default settings if fetch fails
      setSettings(defaultSettings)
    } finally {
      setLoading(false)
    }
  }

  const refreshSettings = async () => {
    await loadSettings()
  }

  useEffect(() => {
    loadSettings()
  }, [])

  return (
    <SettingsContext.Provider value={{ settings, loading, error, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}
